import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { periodParams } from "@/lib/reportsApi";
import { REPORTS } from "@/constants/testIds";
import ExportButtons from "@/components/reports/ExportButtons";
import RevenueChart from "@/components/reports/RevenueChart";

function BreakdownTable({ title, rows, testId }) {
  return (
    <div className="bg-white border border-border rounded-xl p-5" data-testid={testId}>
      <h3 className="text-sm font-medium text-foreground mb-3">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Sin datos en este periodo.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between text-sm py-1.5 border-b border-border/60 last:border-0">
              <span className="text-foreground">{r.label}</span>
              <span className="font-medium text-foreground">${r.amount.toFixed(2)} <span className="text-muted-foreground">({r.count})</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Reporte de Ingresos: serie del periodo + desglose por servicio,
 * profesional y método de pago. Exportable a Excel/CSV (detalle transaccional). */
export default function RevenueTab({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (period.period === "custom" && (!period.start || !period.end)) return;
    setLoading(true);
    api.get("/reports/revenue", { params: periodParams(period) })
      .then(({ data }) => setData(data))
      .catch((err) => toast({ title: "No se pudo cargar Ingresos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !data) return <p className="text-center text-muted-foreground py-12">Cargando ingresos...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">Total del periodo: <span className="font-serif text-xl text-foreground" data-testid={REPORTS.revenueTotal}>${data.total.toFixed(2)}</span></p>
        <ExportButtons reportKey="revenue" period={period} />
      </div>
      <RevenueChart data={data.series} testId={REPORTS.revenueBySeries} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownTable title="Por servicio" rows={data.by_service} testId={REPORTS.revenueByService} />
        <BreakdownTable title="Por profesional" rows={data.by_professional} testId={REPORTS.revenueByProfessional} />
        <BreakdownTable title="Por método de pago" rows={data.by_payment_method} testId={REPORTS.revenueByMethod} />
      </div>
    </div>
  );
}
